import * as XLSX from 'xlsx-js-style';
import type Handsontable from 'handsontable';
import type { ExportToExcelOptions, HeaderColorMaps } from './types';
import {
  projectNestedHeaders,
  buildMerges,
  type VisibleHeaderGrid,
} from './headerProjection';
import { numbroToExcelFormat } from './cellFormat';
import {
  HEADER_STYLE,
  ROW_HEADER_STYLE,
  DATA_STYLE,
  coloredHeaderStyle,
} from './cellStyles';

type CellValue = string | number | boolean | null;

interface ColumnFormat {
  /** Excel format string, e.g. '#,##0.00' or '0.00%'. */
  numFmt?: string;
  /** Если true — значения форсируются как Number (несмотря на string из HoT). */
  isNumeric: boolean;
}

interface ExtractedTable {
  data: CellValue[][];
  rowHeaders: (string | number)[];
  flatColHeaders: string[];
  headerGrid: VisibleHeaderGrid | null;
  /** Соответствие visible-индекса → исходный индекс колонки. */
  visibleCols: number[];
  /** По одному элементу на ВИДИМУЮ колонку — формат данных в этой колонке. */
  columnFormats: ColumnFormat[];
}

// eslint-disable-next-line no-control-regex
const FORBIDDEN_FILENAME_CHARS = /[\\/:*?"<>|\x00-\x1f]/g;
const MAX_FILENAME_LENGTH = 200;

function sanitizeFileName(name: string): string {
  const cleaned = name.replace(FORBIDDEN_FILENAME_CHARS, '').trim();
  return (cleaned || 'export').slice(0, MAX_FILENAME_LENGTH);
}

function readColumnFormat(hot: Handsontable, originalCol: number, totalRows: number): ColumnFormat {
  if (totalRows === 0) return { isNumeric: false };
  const meta = hot.getCellMeta(0, originalCol) as Handsontable.CellMeta & {
    type?: string;
    numericFormat?: { pattern?: unknown };
  };
  const isNumeric = meta.type === 'numeric' || !!meta.numericFormat;
  if (!isNumeric) return { isNumeric: false };
  return {
    isNumeric: true,
    numFmt: numbroToExcelFormat(meta.numericFormat?.pattern),
  };
}

function extractTable(hot: Handsontable): ExtractedTable {
  const totalRows = hot.countRows();
  const totalCols = hot.countCols();

  const hiddenColumns = hot.getSettings().hiddenColumns;
  const hiddenSet = new Set<number>(
    Array.isArray(hiddenColumns) ? (hiddenColumns as number[]) : []
  );

  const visibleCols: number[] = [];
  for (let i = 0; i < totalCols; i++) {
    if (!hiddenSet.has(i)) visibleCols.push(i);
  }
  if (visibleCols.length === 0) {
    for (let i = 0; i < totalCols; i++) visibleCols.push(i);
  }

  const allData = hot.getData();
  const data: CellValue[][] = [];
  for (let r = 0; r < totalRows; r++) {
    const row: CellValue[] = [];
    for (const c of visibleCols) {
      const v = allData[r]?.[c];
      row.push(v === undefined ? null : (v as CellValue));
    }
    data.push(row);
  }

  const rowHeaders: (string | number)[] = [];
  for (let r = 0; r < totalRows; r++) {
    const h = hot.getRowHeader(r);
    rowHeaders.push(h !== undefined ? (h as string | number) : r + 1);
  }

  const flatColHeaders: string[] = visibleCols.map((c) => {
    const h = hot.getColHeader(c);
    return h ? String(h) : `Column ${c + 1}`;
  });

  const nestedHeaders = hot.getSettings().nestedHeaders;
  const headerGrid =
    nestedHeaders && Array.isArray(nestedHeaders) && nestedHeaders.length > 0
      ? projectNestedHeaders(nestedHeaders, visibleCols)
      : null;

  const columnFormats = visibleCols.map((c) => readColumnFormat(hot, c, totalRows));

  return { data, rowHeaders, flatColHeaders, headerGrid, visibleCols, columnFormats };
}

const EXCEL_COLUMN_WIDTH = {
  DEFAULT: 10,
  MAX: 50,
  PADDING: 2,
} as const;

function computeColWidths(rows: CellValue[][]): { wch: number }[] {
  const widths: { wch: number }[] = [];
  for (const row of rows) {
    row.forEach((cell, colIndex) => {
      const text = cell == null ? '' : String(cell);
      const current = widths[colIndex]?.wch ?? EXCEL_COLUMN_WIDTH.DEFAULT;
      widths[colIndex] = {
        wch: Math.max(
          current,
          Math.min(text.length + EXCEL_COLUMN_WIDTH.PADDING, EXCEL_COLUMN_WIDTH.MAX)
        ),
      };
    });
  }
  return widths;
}

interface BuildSheetParams {
  /** Если задан — шапка пишется из grid (учёт цветов и originalIndexInLevel). */
  headerGrid: VisibleHeaderGrid | null;
  /** Используется когда headerGrid отсутствует (плоская шапка). */
  flatColHeaders: string[];
  /** Соответствие visible-индекса → исходный индекс колонки (для look-up цветов). */
  visibleCols: number[];
  dataRows: CellValue[][];
  rowHeaderValues: (string | number)[];
  /** По одному элементу на ВИДИМУЮ data-колонку (без row-header). */
  columnFormats: ColumnFormat[];
  includeRowHeaders: boolean;
  merges: ReturnType<typeof buildMerges>;
  headerColors?: HeaderColorMaps;
}

type CellStyle = ReturnType<typeof coloredHeaderStyle>;

function resolveTopLevelColor(
  originalIndexInLevel: number,
  topLevel: Map<number, string> | undefined,
  dynamicFallback: boolean | undefined
): string | undefined {
  if (!topLevel || topLevel.size === 0) return undefined;
  const direct = topLevel.get(originalIndexInLevel);
  if (direct) return direct;
  if (!dynamicFallback) return undefined;
  let maxKey = -1;
  for (const k of topLevel.keys()) if (k > maxKey) maxKey = k;
  if (maxKey >= 0 && originalIndexInLevel > maxKey) return topLevel.get(maxKey);
  return undefined;
}

function pickHeaderStyle(color: string | undefined): CellStyle {
  return color ? coloredHeaderStyle(color) : HEADER_STYLE;
}

/**
 * Собирает worksheet вручную (без aoa_to_sheet), чтобы прикрепить стили,
 * числовые форматы и типы ячеек.
 */
function buildWorksheet({
  headerGrid,
  flatColHeaders,
  visibleCols,
  dataRows,
  rowHeaderValues,
  columnFormats,
  includeRowHeaders,
  merges,
  headerColors,
}: BuildSheetParams): XLSX.WorkSheet {
  const ws: XLSX.WorkSheet = {};
  const rowHeaderCols = includeRowHeaders ? 1 : 0;

  const headerRowCount = headerGrid ? headerGrid.levels.length : 1;
  const dataRowCount = dataRows.length;
  const totalRows = headerRowCount + dataRowCount;
  const visibleDataCols = headerGrid
    ? headerGrid.totalVisibleCols
    : flatColHeaders.length;
  const totalCols = visibleDataCols + rowHeaderCols;

  const writeHeader = (r: number, c: number, label: string, style: CellStyle) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    ws[addr] = { v: label, t: 's', s: style };
  };

  // Левая верхняя «угловая» ячейка (над колонкой row-headers) — нейтральный стиль.
  if (includeRowHeaders) {
    for (let r = 0; r < headerRowCount; r++) {
      writeHeader(r, 0, '', HEADER_STYLE);
    }
  }

  if (headerGrid) {
    const lastLevel = headerGrid.levels.length - 1;

    headerGrid.levels.forEach((entries, levelIdx) => {
      // Сначала заполним «пустые» промежутки нейтральным стилем,
      // чтобы рамки были везде в строке шапки.
      for (let vc = 0; vc < headerGrid.totalVisibleCols; vc++) {
        writeHeader(levelIdx, vc + rowHeaderCols, '', HEADER_STYLE);
      }

      for (const entry of entries) {
        let color: string | undefined;
        if (levelIdx === 0 && headerGrid.levels.length > 1) {
          // Top-level группа.
          color = resolveTopLevelColor(
            entry.originalIndexInLevel,
            headerColors?.topLevel,
            headerColors?.dynamicTopFallback
          );
        } else if (levelIdx === lastLevel) {
          // Leaf-level: цвет по исходной колонке.
          color = headerColors?.leafColumn?.get(entry.originalStartCol);
        }
        const style = pickHeaderStyle(color);

        // Метка пишется в первую ячейку группы; для merge остальные пустые
        // (но со стилем — иначе бордер по краям merge не отрисуется в Excel
        // на некоторых версиях).
        const startCol = entry.startVisibleCol + rowHeaderCols;
        writeHeader(levelIdx, startCol, entry.label, style);
        for (let k = 1; k < entry.colspan; k++) {
          writeHeader(levelIdx, startCol + k, '', style);
        }
      }
    });
  } else {
    flatColHeaders.forEach((label, dc) => {
      const c = dc + rowHeaderCols;
      const originalCol = visibleCols[dc] ?? dc;
      const color = headerColors?.leafColumn?.get(originalCol);
      writeHeader(0, c, label, pickHeaderStyle(color));
    });
  }

  // Data rows (+ optional row-header column)
  dataRows.forEach((row, dr) => {
    const r = headerRowCount + dr;

    if (includeRowHeaders) {
      const v = rowHeaderValues[dr];
      const addr = XLSX.utils.encode_cell({ r, c: 0 });
      ws[addr] = {
        v: v ?? '',
        t: typeof v === 'number' ? 'n' : 's',
        s: ROW_HEADER_STYLE,
      };
    }

    row.forEach((value, dc) => {
      const c = dc + rowHeaderCols;
      const addr = XLSX.utils.encode_cell({ r, c });
      const fmt = columnFormats[dc];

      if (value == null || value === '') {
        ws[addr] = { v: '', t: 's', s: DATA_STYLE };
        return;
      }

      // Числовой столбец: пытаемся привести строку к числу.
      if (fmt?.isNumeric) {
        const num =
          typeof value === 'number'
            ? value
            : typeof value === 'string'
              ? Number(value.replace(',', '.'))
              : Number.NaN;
        if (Number.isFinite(num)) {
          ws[addr] = {
            v: num,
            t: 'n',
            s: DATA_STYLE,
            ...(fmt.numFmt ? { z: fmt.numFmt } : {}),
          };
          return;
        }
      }

      // Дефолтное определение типа.
      if (typeof value === 'number') {
        ws[addr] = { v: value, t: 'n', s: DATA_STYLE };
      } else if (typeof value === 'boolean') {
        ws[addr] = { v: value, t: 'b', s: DATA_STYLE };
      } else {
        ws[addr] = { v: String(value), t: 's', s: DATA_STYLE };
      }
    });
  });

  if (totalRows > 0 && totalCols > 0) {
    ws['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: totalRows - 1, c: totalCols - 1 },
    });
  }

  if (merges.length > 0) ws['!merges'] = merges;

  // Autofilter — только если есть и шапка, и хотя бы одна data-строка.
  if (headerRowCount > 0 && dataRowCount > 0 && totalCols > 0) {
    const filterStartRow = headerRowCount - 1; // последняя строка шапки (leaf-level)
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: filterStartRow, c: 0 },
        e: { r: totalRows - 1, c: totalCols - 1 },
      }),
    };
  }

  return ws;
}

function downloadWorkbook(workbook: XLSX.WorkBook, fileName: string): void {
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export function exportToExcel(
  hotInstance: Handsontable | null,
  options: ExportToExcelOptions = {}
): void {
  if (!hotInstance) {
    throw new Error('Handsontable instance is not available');
  }

  const {
    fileName = 'export',
    sheetName = 'Sheet1',
    includeRowHeaders = true,
  } = options;

  const { data, rowHeaders, flatColHeaders, headerGrid, visibleCols, columnFormats } =
    extractTable(hotInstance);

  if (data.length === 0 && flatColHeaders.length === 0 && !headerGrid) {
    throw new Error('Нет данных для экспорта');
  }

  const rowHeaderCols = includeRowHeaders ? 1 : 0;
  const merges = headerGrid ? buildMerges(headerGrid, rowHeaderCols) : [];

  const worksheet = buildWorksheet({
    headerGrid,
    flatColHeaders,
    visibleCols,
    dataRows: data,
    rowHeaderValues: rowHeaders,
    columnFormats,
    includeRowHeaders,
    merges,
    headerColors: options.headerColors,
  });

  // Для расчёта ширин используем только leaf-уровень шапки + данные.
  const leafHeaderRow: CellValue[] = [];
  if (includeRowHeaders) leafHeaderRow.push('');
  if (headerGrid) {
    const filled = new Array<string>(headerGrid.totalVisibleCols).fill('');
    const leafEntries = headerGrid.levels[headerGrid.levels.length - 1] ?? [];
    for (const entry of leafEntries) {
      filled[entry.startVisibleCol] = entry.label;
    }
    filled.forEach((v) => leafHeaderRow.push(v));
  } else {
    flatColHeaders.forEach((h) => leafHeaderRow.push(h));
  }
  const widthRows: CellValue[][] = [
    leafHeaderRow,
    ...data.map((row, ri) => {
      const out: CellValue[] = [];
      if (includeRowHeaders) out.push(rowHeaders[ri]);
      row.forEach((v) => out.push(v));
      return out;
    }),
  ];
  worksheet['!cols'] = computeColWidths(widthRows);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  downloadWorkbook(workbook, sanitizeFileName(fileName));
}
